import { Button, Card, Col, ColorPicker, ColorPickerProps, Empty, Input, InputRef, Row, Spin, Tooltip } from "antd";
import { AggregationColor } from "antd/es/color-picker/color";
import { CheckOutlined, DeleteOutlined, LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { CSSProperties, useRef, useState } from "react";
import { CustomLinkColor, UpdateCustomLinkColorDTO } from "@dto/customLinks.dto";
import { ColorUtil } from "src/util/ColorUtil";
import "./CustomLinksFormList.less";

// shared base style for the square swatch buttons (saved colors + "add" button)
const swatchStyle: CSSProperties = {
  width: '1.4em',
  height: '1.4em',
  padding: 0,
  display: 'grid',
  placeItems: 'center',
  borderRadius: '0.2em',
  cursor: 'pointer',
};

const centerStyle: CSSProperties = { display: 'grid', placeItems: 'center' };

interface Props extends Pick<ColorPickerProps, 'value' | 'onChange'> {
  colors: CustomLinkColor[];
  loading: boolean;
  onChangeColors: (colors: UpdateCustomLinkColorDTO[]) => void | Promise<void>;
  onOpenChange?: (open: boolean) => void;
}

const ColorTrigger = ({ value, onChange, colors: colorsProp, loading, onChangeColors, onOpenChange }: Props) => {
  const [colorsPrev, setColorsPrev] = useState(colorsProp);
  const [colors, setColors] = useState(colorsProp);

  if (colorsProp !== colorsPrev) {
    setColorsPrev(colorsProp);
    setColors(colorsProp);
  }

  const [editState, setEditState] = useState<'view' | 'edit' | 'saving'>('view');
  const editMode = editState === 'edit';
  const [open, setOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [addingColor, setAddingColor] = useState(false);
  const inputRefs = useRef<Record<string, InputRef | null>>({});

  const pickPreset = (hex: string) => onChange?.(new AggregationColor(hex), hex);
  const removeColor = (id: string) => setColors(prev => prev.filter(c => c.id !== id));

  const currentHex = typeof value === 'string' ? value : undefined;
  const colorAlreadySaved = !!currentHex && colors.some(c => c.color.toLowerCase() === currentHex.toLowerCase());

  const addCurrentColor = () => {
    const hex = currentHex;
    if (!hex || addingColor || colorAlreadySaved)
      return;

    setAddingColor(true);
    void Promise.resolve(onChangeColors([...colors, { color: hex, description: undefined }])).finally(() => setAddingColor(false));
  };

  const saveEdit = () => {
    const changeWasMade = colors.length !== colorsProp.length
      || colors.some((c, i) => c.id !== colorsProp[i].id
        || c.color !== colorsProp[i].color
        || (c.description ?? '') !== (colorsProp[i].description ?? ''));

    if (!changeWasMade) {
      setEditState('view');
      return;
    }

    setEditState('saving');
    void Promise.resolve(onChangeColors(colors.map(({ id: _, ...rest }) => rest))).finally(() => setEditState('view'));
  };

  const handleToggleEditMode = () => {
    if (editMode) {
      saveEdit();
      return;
    }

    setEditState('edit');
  };

  const handleOpenChange = (state: boolean) => {
    onOpenChange?.(state);
    setOpen(state);
    if (!state && editMode)
      saveEdit();
  };

  const panelRender: ColorPickerProps['panelRender'] = (_, { components: { Picker } }) => (
    <Row className="xm-color-panel" justify="space-between" align="stretch" wrap={false}>
      <Col span={11} style={{ display: 'flex', flexDirection: 'column' }}>
        <Card size="small" style={{ flex: 1 }} styles={{ body: { padding: 0, height: '100%', display: 'flex', flexDirection: 'column' } }}>
          <div className="xm-saved-colors-header">
            <span style={{ fontWeight: 500 }}>Saved colors</span>
            <Button type={editMode ? 'primary' : 'default'} size="small" loading={editState === 'saving' ? { delay: 500 } : false} onClick={handleToggleEditMode}>Edit</Button>
          </div>
          <div style={{ padding: '0.6em', flex: 1 }}>
            {loading
              ? <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}><Spin size="small" /></div>
              : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 1.4em)', gap: '6px', justifyContent: 'space-between' }}>
                {colors.map(c => {
                  const selected = typeof value === 'string' && value.toLowerCase() === c.color.toLowerCase();
                  return (
                    <Tooltip key={c.id} title={c.description} open={editMode ? false : undefined}>
                      <button
                        type="button"
                        aria-label={c.description || c.color}
                        onClick={() => editMode ? inputRefs.current[c.id]?.focus() : pickPreset(c.color)}
                        style={{
                          ...swatchStyle,
                          background: c.color,
                          border: '1px solid var(--ant-color-border)',
                          boxShadow: hoveredId === c.id || focusedId === c.id ? `0 0 0 2px var(--ant-color-bg-container), 0 0 0 4px ${c.color}` : undefined,
                          transform: hoveredId === c.id || focusedId === c.id ? 'scale(0.643)' : undefined,
                          transition: 'box-shadow 0.15s ease, transform 0.15s ease',
                        }}
                      >
                        {selected && !editMode && <CheckOutlined style={{ color: ColorUtil.contrastText(c.color), fontSize: '0.8em' }} />}
                      </button>
                    </Tooltip>
                  );
                })}
                {!editMode &&
                  <Tooltip title={colorAlreadySaved ? 'Color is already saved' : 'Add current color'}>
                    <button
                      type="button"
                      aria-label="Add current color"
                      aria-disabled={colorAlreadySaved}
                      onClick={addCurrentColor}
                      style={{
                        ...swatchStyle,
                        background: 'transparent',
                        border: '1px solid var(--ant-color-border)',
                        ...(colorAlreadySaved && { color: 'var(--ant-color-text-disabled)', background: 'var(--ant-color-bg-container-disabled)', cursor: 'not-allowed' }),
                      }}
                    >
                      <Spin spinning={addingColor} delay={500} styles={{ root: centerStyle, container: centerStyle }} indicator={<LoadingOutlined spin style={{ fontSize: '0.8em' }} />}>
                        <PlusOutlined style={{ fontSize: '0.8em' }} />
                      </Spin>
                    </button>
                  </Tooltip>
                }
              </div>}
          </div>
        </Card>
      </Col>
      <Col flex="auto" style={{ marginLeft: '1em' }}>
        <Card size="small" style={{ height: '100%' }} styles={{ body: { padding: '0.6em', position: 'relative', height: '100%' } }}>
          <Picker />
          {editMode && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 10, background: 'var(--ant-color-bg-container)', padding: '0.6em', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5em' }}>
              {colors.length === 0 && (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No saved colors yet."
                  style={{ margin: 'auto' }}
                />
              )}
              {colors.map(c => (
                <div
                  key={c.id}
                  className="xm-color-row"
                  onMouseEnter={() => setHoveredId(c.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  style={{ flexShrink: 0 }}
                >
                  <div style={{ display: 'grid', padding: hoveredId === c.id || focusedId === c.id ? '0.2em' : 0, transition: 'padding 0.15s ease' }}>
                    <span style={{ background: c.color, borderRadius: '0.2em' }} />
                  </div>
                  <Input
                    ref={el => { inputRefs.current[c.id] = el; }}
                    className="xm-sep-left"
                    variant="borderless"
                    size="small"
                    placeholder="Description"
                    value={c.description ?? ''}
                    onChange={e => setColors(prev => prev.map(x => x.id === c.id ? { ...x, description: e.target.value } : x))}
                    onFocus={() => setFocusedId(c.id)}
                    onBlur={() => setFocusedId(null)}
                    style={{ borderRadius: 0 }}
                  />
                  <Button
                    type="text"
                    danger
                    size="small"
                    className="xm-link-delete xm-sep-left"
                    icon={<DeleteOutlined />}
                    onClick={() => removeColor(c.id)}
                    style={{ borderRadius: 0 }}
                  />
                </div>
              ))}
            </div>
          )}
        </Card>
      </Col>
    </Row>
  );

  return <ColorPicker
    value={value}
    onChange={onChange}
    disabledAlpha
    format="hex"
    panelRender={panelRender}
    styles={{ popupOverlayInner: { width: '32em' } }}
    onOpenChange={handleOpenChange}
  >
    <Button
      title="Link color"
      className={open ? 'xm-link-color xm-open' : 'xm-link-color'}
      style={{ width: '100%', height: '100%', padding: 0, border: 'none', borderRadius: '0.1em', background: value as string, cursor: 'pointer' }}
    />
  </ColorPicker>;
};

export default ColorTrigger;
